import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BaseDto } from "../../util/baseDto";
import { useNotification } from "../../../components/common/GlobalNotification";
import { notifications_api_requests } from "..";

const PATH = "/notifications";

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Notification Template
 * Represents a notification template for a specific channel-provider combination.
 *
 * Templates are now associated with both channel AND provider because:
 * - Templates are channel-specific (SMS vs email have different formats)
 * - Templates are provider-specific (different providers may have different template formats/requirements)
 * - A provider can support multiple channels, so templates need both identifiers
 */
export interface NotificationTemplate extends BaseDto {
  templateCode: string;
  templateName: string;
  channelCode: string;
  channelName: string; // Channel display name for consistency with API response
  providerCode: string; // Required - templates are provider-specific
  providerName: string; // Provider display name for consistency with API response
  subject: string | null;
  body: string;
  isEnabled: boolean;
  variables: string[] | null;
  // Optional fields from API
  providerTemplateId?: string; // External provider template ID
  description?: string; // Template description
  language?: string; // Template language (default "en")
  senderId?: string; // Sender ID for the template
  createdAt: string;
  updatedAt: string;
}

/**
 * Request body for creating a notification template
 *
 * Note: providerCode is required because templates are channel-provider specific.
 * Different providers may have different template formats and requirements.
 */
export interface CreateNotificationTemplateRequest {
  templateCode: string;
  templateName: string;
  channelCode: string;
  providerCode: string; // Required - templates are channel-provider specific
  subject?: string;
  templateContent: string;
  isEnabled: boolean;
  variables?: string[];
  // Optional fields
  providerTemplateId?: string; // External provider template ID
  description?: string; // Template description
  language?: string; // Template language (default "en")
  senderId?: string; // Sender ID for the template
}

/**
 * Request body for updating a notification template
 * All fields from CreateNotificationTemplateRequest are optional except id
 */
export type UpdateNotificationTemplateRequest =
  Partial<CreateNotificationTemplateRequest> & {
    id: number;
  };

// ============================================================================
// Query Keys
// ============================================================================

export const NOTIFICATION_TEMPLATES_QUERY_KEY = "notifications-templates";

// ============================================================================
// Template Management Hooks
// ============================================================================

/**
 * Fetch all notification templates
 * GET /api/v1/notifications/templates
 * @note This is a placeholder structure for future implementation
 */
export const useGetTemplates = () => {
  return useQuery({
    queryKey: [NOTIFICATION_TEMPLATES_QUERY_KEY, "all"],
    queryFn: async (): Promise<NotificationTemplate[]> =>
      notifications_api_requests.get(`${PATH}/templates`),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetch a specific notification template by template code
 * GET /api/v1/notifications/templates/{templateCode}
 * @param templateCode - The template code
 * @note This is a placeholder structure for future implementation
 */
export const useGetTemplate = (templateCode?: string) => {
  return useQuery({
    queryKey: [NOTIFICATION_TEMPLATES_QUERY_KEY, "by-code", templateCode],
    queryFn: async (): Promise<NotificationTemplate> =>
      notifications_api_requests.get(`${PATH}/templates/${templateCode}`),
    enabled: !!templateCode,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Create a new notification template
 * POST /api/v1/notifications/templates
 * @note This is a placeholder structure for future implementation
 */
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (
      request: CreateNotificationTemplateRequest,
    ): Promise<NotificationTemplate> =>
      notifications_api_requests.post(`${PATH}/templates`, request),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_TEMPLATES_QUERY_KEY],
      });
      setSuccessNotification("Template created successfully");
    },

    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to create template";
      setErrorNotification(message);
    },
  });
};

/**
 * Update an existing notification template
 * PUT /api/v1/notifications/templates/{templateCode}
 * @note This is a placeholder structure for future implementation
 */
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      templateCode,
      request,
    }: {
      templateCode: string;
      request: UpdateNotificationTemplateRequest;
    }): Promise<NotificationTemplate> =>
      notifications_api_requests.put(
        `${PATH}/templates/${templateCode}`,
        request,
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_TEMPLATES_QUERY_KEY],
      });
      setSuccessNotification("Template updated successfully");
    },

    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to update template";
      setErrorNotification(message);
    },
  });
};

/**
 * Delete a notification template
 * DELETE /api/v1/notifications/templates/{templateCode}
 * @note This is a placeholder structure for future implementation
 */
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (templateCode: string): Promise<void> =>
      notifications_api_requests.delete(`${PATH}/templates/${templateCode}`),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_TEMPLATES_QUERY_KEY],
      });
      setSuccessNotification("Template deleted successfully");
    },

    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to delete template";
      setErrorNotification(message);
    },
  });
};

// Made with Bob
